/**
 * STITCH-2 SCOPE 5 -- the drawer's stage-aware action strip. Renders the
 * founder matrix (stageAwareActions) for the lead's CURRENT stage over the
 * EXISTING stage write path (POST /api/leads/:id/stage via the injected
 * onAdvance); the backend's config-consulted stage authority remains the
 * security boundary (an unauthorized caller gets the server's own denial
 * message here, never a silent success). No Reschedule, no No-show (V4-P1).
 */

import { useState } from 'react';

import type { LeadStageTarget } from '@/dashboard/api';
import {
  stageAwareActions,
  workboardActionErrorMessage,
} from '@/dashboard/workboard/workboard-view';

export interface QuickViewStageStripProps {
  readonly stage: string | null | undefined;
  readonly onAdvance: (to: LeadStageTarget) => Promise<void>;
}

export default function QuickViewStageStrip({
  stage,
  onAdvance,
}: QuickViewStageStripProps): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const actions = stageAwareActions(stage);
  if (actions.length === 0) return null;

  return (
    <span className="quickview-stage-strip" data-testid="quickview-stage-strip">
      {actions.map((a) => (
        <button
          key={a.to}
          type="button"
          className={
            a.primary
              ? 'ac-btn ac-btn--primary ac-btn--sm'
              : 'ac-btn ac-btn--secondary ac-btn--sm'
          }
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            onAdvance(a.to)
              .catch((err: unknown) => {
                setError(workboardActionErrorMessage(err));
              })
              .finally(() => setBusy(false));
          }}
        >
          {a.label}
        </button>
      ))}
      {error !== null ? (
        <span className="lead-quickview__readonly" data-testid="quickview-stage-error">
          {error}
        </span>
      ) : null}
    </span>
  );
}
